import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import IconTooltipButton from '../../../../button/IconTooltipButton';
import Module from '../../../../layout/Module';
import FilterButton from '../Filter/Button';
import Checkbox from '@mui/material/Checkbox';
import ButtonStartIcon from '../../../../button/ButtonStartIcon';
import PhotoManagementTable from './Table';
import PhotoManagementGrid from './Grid';
import { Alert } from '@mui/material';
import FormatDate from '../../../../date/FormatDate';
import DialogAction from '../Dialog/Dialog';
import { getPhotos } from '../../../../../services/photo-management/photo-management.service';
import TablePagination from '@mui/material/TablePagination';
import PhotoManagementPagination from './Pagination';
import { formatBytes } from '../../../../../utils/mimetype';

const PhotoManagementList = ({ view }) => {
  const permissions = useSelector((state) => state?.session?.permissions?.['photo-management'] || {});

  const [isLoadingList, setIsLoadingList] = useState([]);
  const [list, setList] = useState([]);
  const [tempList, setTempList] = useState([]);
  const [errorList, setErrorList] = useState(null);
  const [checkIds, setCheckIds] = useState([]);
  const [modal, setModal] = useState(null);
  const [change, setChange] = useState(false);
  const [skip, setSkip] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const handleChangePage = (event, newPage) => {
    event.preventDefault();
    setSkip(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    event.preventDefault();
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
  };

  const loadData = useCallback(async () => {
    try {
      setIsLoadingList(true);
      const response = await getPhotos();
      if (response.error) {
        setErrorList(response?.error?.message || 'Something went wrong.');
      } else {
        setList(response.data);
        setTempList(response.data);
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      setErrorList(message);
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [change, loadData]);
  useEffect(() => {
    setCheckIds([]);
  }, [list]);

  const onSelectedAll = (checked) => {
    if (checked) {
      setCheckIds(list.map((row) => row.fullName));
    } else {
      setCheckIds([]);
    }
  };
  const onSelected = (id, checked) => {
    if (checked) {
      setCheckIds([...checkIds, id]);
    } else {
      setCheckIds(checkIds.filter((item) => item !== id));
    }
  };

  const onFilter = (value = '') => {
    setSkip(0);
    if (value) {
      setList(tempList.filter((row) => row?.userCode?.toLowerCase().includes(value?.toLowerCase())));
    } else {
      setList(tempList);
    }
  };

  useEffect(() => {
    setList(tempList || []);
  }, [tempList]);

  const listData = useMemo(() => {
    return list.slice(skip * rowsPerPage, skip * rowsPerPage + rowsPerPage).map((row, index) => ({
      ...row,
      _id: `${row._id}-${index}`,
      index: skip * rowsPerPage + index + 1,
      name: row.fullName,
      lastModified: <FormatDate date={row.lastModified} pattern='dd/MMM/yyyy hh:mm:ss a' />,
      checkIds: (
        <Checkbox
          id={`checkbox-id-user-${row._id}`}
          checked={checkIds.includes(row.fullName)}
          onChange={(e) => onSelected(row.fullName, e.target.checked)}
          color='primary'
        />
      ),
      size: formatBytes(row.size),
      deleted: (
        <>
          {permissions.deleted && (
            <IconTooltipButton
              id={`delete-${row._id}`}
              disabled={isLoadingList}
              title='Delete'
              icon='trash-alt'
              onClick={() => setModal({ type: 'delete', data: row })}
            />
          )}
        </>
      ),
      actions: (
        <div className='flex items-center gap-1 items-center justify-center'>
          {permissions?.preview && (
            <IconTooltipButton
              id={`preview-${row._id}`}
              disabled={isLoadingList}
              title='Preview'
              icon='expand-alt'
              onClick={() => setModal({ type: 'preview', data: row })}
            />
          )}
          {permissions.deleted && (
            <IconTooltipButton
              id={`delete-${row._id}`}
              disabled={isLoadingList}
              title='Delete'
              icon='trash-alt'
              onClick={() => setModal({ type: 'delete', data: row })}
            />
          )}
        </div>
      )
    }));
  }, [checkIds, isLoadingList, list, permissions.deleted, permissions.preview, skip, rowsPerPage]);

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary justify-between'>
        <FilterButton onFilter={onFilter} />
        <div>
          {checkIds.length > 1 && permissions['deleted-bulk'] && (
            <ButtonStartIcon
              startIcon='trash-alt'
              onClick={() => setModal({ type: 'bulk delete', data: { _id: checkIds } })}
              color='primary'
            >
              Delete
            </ButtonStartIcon>
          )}
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer classNameWrapper='overflow-auto'>
        {errorList ? (
          <Alert severity='error'>{errorList}</Alert>
        ) : (
          <>
            <PhotoManagementPagination
              totalList={list.length}
              skip={skip}
              rowsPerPage={rowsPerPage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleChangePage={handleChangePage}
            />
            {view === 'list' && (
              <PhotoManagementTable
                isLoadingList={isLoadingList}
                columns={[
                  { label: '#', key: 'index', align: 'center', width: 10, show: true, hide: false },
                  {
                    show: permissions['deleted-bulk'],
                    key: 'checkIds',
                    align: 'center',
                    width: 15,
                    label: (
                      <Checkbox
                        id={`checkbox-id-user-header`}
                        checked={checkIds.length > 0 && checkIds.length === tempList.length}
                        indeterminate={checkIds.length > 0 && checkIds.length < tempList.length}
                        onChange={(e) => onSelectedAll(e.target.checked)}
                        color='primary'
                      />
                    )
                  },
                  { label: 'User code', key: 'userCode', show: true, hide: false },
                  { label: 'Name', key: 'name', show: true, hide: false },
                  { label: 'Extension', key: 'extension', show: true, hide: false },
                  { label: 'Size', key: 'size', show: true, hide: false },
                  { label: 'Last modified', key: 'lastModified', show: true, hide: false },
                  { label: 'Actions', key: 'actions', align: 'center', width: 50, show: true, hide: false }
                ]}
                list={listData}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleChangePage={handleChangePage}
                skip={skip}
                totalList={list.length}
              />
            )}
            {view === 'grid' && <PhotoManagementGrid isLoadingList={isLoadingList} list={listData} />}
          </>
        )}
      </Module.BodyContainer>
      {modal && (
        <DialogAction
          show={!!modal}
          onClose={() => setModal(null)}
          onSave={() => setChange(!change)}
          type={modal?.type}
          form={modal?.data}
        />
      )}
    </>
  );
};

export default PhotoManagementList;
